import DisneyRewardsEventCatalog from './analytics/event-catalog';
import { sendAnalyticsData, sanitizeValue, initializeDigitalData, updateDigitalDataEvent, pushToDataLayer, getOneIdData, trackOneIdEvent } from './analytics/utilities';
import { contentWaitTimeout } from './utilities/static';
import { getCell } from './session-storage';
import { addCustomEvents } from './utilities/custom-events';

class DisneyRewardsAnalytics extends DisneyRewardsEventCatalog {
    // 2 seconds, or 2,000 milliseconds. Default wait time before sending analytics data.
    analyticsTimeout = 2000;

    // Store video tracking observer to prevent memory leaks
    videoTrackingObserver = null;

    constructor() {
        super();

        // Bind event handlers to preserve 'this' context when used as callbacks
        this.handleEventElementClick = this.handleEventElementClick.bind(this);

        this.addAnalyticsAttributesToElements(['main a[href]', 'main [data-modal]', 'main input[type="submit"]']);

        this.addAnalyticsAttributesToNavigation();

        this.addAnalyticsAttributesToFooterLinks();

        this.manageAnalyticsEventListeners();

        this.bindEvents();

        const observer = new MutationObserver(() => {
            this.initializeSocialShareTracking();
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    // ============================================================
    // HELPER METHODS
    // ============================================================

    /**
     * Unified method to track events in both ACDL and legacy formats.
     * Reduces code duplication across event handlers.
     * @param {string} acdlEventName - The ACDL event name
     * @param {Object} acdlData - The ACDL event data
     * @param {Object} legacyData - The legacy event data (optional)
     */
    trackEvent(acdlEventName, acdlData = {}, legacyData = null) {
        // Push to ACDL
        if (window.pushToDataLayer) {
            window.pushToDataLayer({
                event: acdlEventName,
                ...acdlData
            });
        }

        // Push to legacy tracking if provided
        if (legacyData) {
            sendAnalyticsData(legacyData);
        }
    }

    /**
     * Determines the ACDL event name from the legacy event name.
     * @param {string} eventName The legacy event name
     * @returns {string} The ACDL event name
     */
    getAcdlEventName(eventName) {
        const eventMap = {
            'navigation_click': 'navigation clicked',
            'footer_click': 'footer link click',
            'content_click': 'content_click',
            'exit_chase': 'exit chase',
        };
        return eventMap[eventName] || eventName;
    }

    /**
     * Determines the link type from the element.
     * @param {HTMLElement} target The clicked element
     * @returns {string} The link type
     */
    getLinkType(target) {
        if (target.dataset.navigation_link_click) return 'navigation';
        if (target.dataset.footer_link_click) return 'footer';
        if (target.dataset.content_link) return 'content';
        if (target.href && target.href.includes('chase.com')) return 'exit';
        return 'link';
    }

    /**
     * Determines the link position from the element.
     * @param {HTMLElement} target The clicked element
     * @returns {string} The link position
     */
    getLinkPosition(target) {
        if (target.closest('header')) return 'header';
        if (target.closest('footer')) return 'footer';
        if (target.closest('nav')) return 'navigation';
        if (target.closest('main')) return 'main-content';
        return 'unknown';
    }

    /**
     * Extracts video metadata from a video element.
     * @param {HTMLVideoElement} video The video element
     * @returns {Object} Video metadata
     */
    getVideoMetadata(video) {
        const videoTitle = video.getAttribute('title') ||
                          video.getAttribute('data-title') ||
                          video.closest('figure')?.querySelector('figcaption')?.textContent?.trim() ||
                          'Untitled Video';
        const videoURL = video.getAttribute('src') ||
                        video.querySelector('source')?.getAttribute('src') ||
                        '';
        const videoDuration = video.duration || 0;

        return {
            videoTitle,
            videoURL,
            videoDuration: Math.round(videoDuration)
        };
    }

    // ============================================================
    // SETUP & INITIALIZATION HELPERS
    // ============================================================

    /**
     * Applies a unique analytics data attribute to each element that matches a
     * provided selector. The attribute is generated from the the element's text
     * content (or value, in the case of an input) and its position on the page
     * relative to any elements with the same value.
     * @param {string[]} selectorArray An array of CSS selector strings for elements to search for and
     *                                 apply attributes to.
     */
    addAnalyticsAttributesToElements(selectorArray) {
        // Format the selector array into a single selector string.
        const selectorString = selectorArray.join(', ');

        // Get a regular array of all matching elements.
        let matchingElements = Array.from(document.querySelectorAll(selectorString));

        // Filter out elements that don't meet certain criteria.
        matchingElements = matchingElements.filter(
            (el) =>
                // Must not have a `data-content_link` attribute value.
                !el.dataset.content_link &&
                // Must not have a `data-navigation_link` attribute value.
                !el.dataset.navigation_link
        );

        if (matchingElements.length) {
            // Create a storage array for values that have already been assigned to
            // ensure that we don't apply any value twice.
            const assignedValues = [];

            matchingElements.forEach((element) => {
                // Pull a value to assign to this element by sanitizing its existing
                // contents or value.
                const generatedValue = sanitizeValue(
                    element.innerText || element.value || document.querySelector('img', element)?.alt || 'no_val'
                );

                // Determine which increment to use in the case of multiple instances of
                // the same value on the page.
                let i = 1;
                while (assignedValues.includes(`${generatedValue}_${i}`)) {
                    i += 1;
                }

                // Store a reference to this instance of this value.
                assignedValues.push(`${generatedValue}_${i}`);

                // Apply the generated attribute value to this element, also giving it
                // an `event_name` if needed.
                if (!element.dataset.event_name) {
                    element.setAttribute('data-event_name', 'content_click');
                }
                element.setAttribute('data-content_link', `${generatedValue}_${i}`);
            });
        }
    }

    /**
     * Applies analytics data attributes to navigation links in the site header.
     * Each link receives a unique data-content_link based on its text and position.
     */
    addAnalyticsAttributesToNavigation() {
        const header = document.querySelector('.dr-site-header');
        if (!header) return;

        const navLinks = Array.from(header.querySelectorAll('a'));

        // Filter out links that already have tracking attributes
        const untaggedLinks = navLinks.filter(
            (link) => !link.dataset.content_link && !link.dataset.navigation_link_click
        );

        if (untaggedLinks.length) {
            untaggedLinks.forEach((link) => {
                if (link.hasAttribute('data-event_name') && (link.getAttribute('data-event_name').includes('navigation'))) {
                    return;
                }

                const linkText = sanitizeValue(
                    link.textContent?.trim() || link.getAttribute('aria-label') || 'nav_link'
                );

                link.setAttribute('data-event_name', 'navigation_link_click');
                link.setAttribute('data-content_link', `${linkText}`);
            });
        }
    }

    /**
     * Applies analytics data attributes to navigation links in the site header.
     * Each link receives a unique data-content_link based on its text and position.
     */
    addAnalyticsAttributesToFooterLinks() {
        const footer = document.querySelector('.dr-footer');
        if (!footer) return;

        const navLinks = Array.from(footer.querySelectorAll('a'));

        // Filter out links that already have tracking attributes
        const untaggedLinks = navLinks.filter(
            (link) => !link.dataset.content_link && !link.dataset.footer_link_click
        );

        if (untaggedLinks.length) {
            untaggedLinks.forEach((link) => {
                if (link.hasAttribute('data-event_name') && (link.getAttribute('data-event_name').includes('footer'))) {
                    return;
                }

                const linkText = sanitizeValue(
                    link.textContent?.trim() || link.getAttribute('aria-label') || 'nav_link'
                );

                // Skip if this is a social share link (already tracked by social share tracking)
                // Check if link or any parent has social-related classes
                const isSocialLink = link.hasAttribute('data-social-tracking-initialized') ||
                    link.hasAttribute('data-social') ||
                    link.hasAttribute('data-share') ||
                    link.closest('.social-item') ||
                    link.closest('.social-list') ||
                    link.className.includes('social') ||
                    link.className.includes('share') ||
                    Array.from(link.classList).some(cls => cls.includes('social') || cls.includes('share'));

                // Also check if any parent element has social/share in class name
                let parent = link.parentElement;
                while (parent && parent !== footer) {
                    if (parent.className && typeof parent.className === 'string' &&
                        (parent.className.includes('social') || parent.className.includes('share'))) {
                        return;
                    }
                    parent = parent.parentElement;
                }

                if (isSocialLink) {
                    return;
                }

                link.setAttribute('data-event_name', 'footer_link_click');
                link.setAttribute('data-content_link', `${linkText}`);
            });
        }
    }

    /**
     * Identify elements that should send "Exit to Chase" events and add the
     * appropriate event handler, which identifies when a guest clicks a link
     * that sends them to Chase's website.
     * @param {HTMLElement} context The HTML element to searching within the DOM.
     */
    addChaseClickEvent(context = document) {
        // Capture 'this' context for use inside event listeners
        const self = this;
        const elements = context.querySelectorAll('a.apply-now-button');

        if (elements.length) {
            elements.forEach((element) => {
                const wasChaseEvent = element.getAttribute('data-was-chase-event');

                if (wasChaseEvent === null) {
                    element.setAttribute('data-was-chase-event', 'true');
                    element.addEventListener('click', () => {
                        const cardType = element.getAttribute('data-card-type') || '';
                        // Check if this is a change card design button based on its visible text
                        const isChangeCardDesignButton = element.textContent?.trim() === 'Change Card Design';

                        // Fire "card_designs_clicked" event for change card design buttons (instead of apply_now_click)
                        if (isChangeCardDesignButton) {
                            if (window.pushToDataLayer) {
                                window.pushToDataLayer({
                                    event: 'card_designs_clicked',
                                    product: {
                                        cardType
                                    },
                                    interaction: {
                                        linkName: element.textContent?.trim() || 'Change Card Design',
                                        linkURL: element.href,
                                        linkType: 'cta',
                                        linkPosition: self.getLinkPosition(element),
                                        module: 'card-designs'
                                    }
                                });
                            }

                            sendAnalyticsData({
                                event_name: 'card_designs_clicked',
                                link_text: element.textContent?.trim() || 'Change Card Design',
                                link_url: element.href,
                                module: 'card-designs'
                            });
                        } else {
                            // Fire "apply now clicked" event for non-change-card-design buttons
                            const buttonText = element.textContent?.trim() || 'Apply Now';

                            // Push to Adobe Client Data Layer (ACDL)
                            if (window.pushToDataLayer) {
                                // Get current product context from ACDL state
                                const currentState = window.adobeDataLayer?.getState ? window.adobeDataLayer.getState() : {};
                                const cardArt = currentState.product?.cardArt || '';

                                window.pushToDataLayer({
                                    event: 'apply_now_click',
                                    product: {
                                        cardType,
                                        cardArt
                                    },
                                    interaction: {
                                        linkName: buttonText,
                                        linkURL: window.location.href,
                                        linkType: 'cta',
                                        linkPosition: 'apply-now-button',
                                        module: 'card-application'
                                    }
                                });
                            }

                            sendAnalyticsData({
                                ...window.disneyRewardsAnalytics.getApplyNowData(element),
                                event_name: 'apply_now_click'
                            });
                        }

                        // Fire "exit chase" event only for actual chase.com links (not chase-interstitial pages)
                        if (element.href && element.href.includes('chase.com')) {
                            const eventData = { event_name: 'exit_chase' };
                            updateDigitalDataEvent(eventData);

                            // Push to ACDL
                            if (window.pushToDataLayer) {
                                window.pushToDataLayer({
                                    event: 'exit_chase',
                                    interaction: {
                                        linkName: element.textContent?.trim() || 'Chase link',
                                        linkURL: element.href,
                                        linkType: 'exit',
                                        linkPosition: self.getLinkPosition(element),
                                        module: 'chase-exit'
                                    }
                                });
                            }

                            sendAnalyticsData(eventData);
                        }
                    });
                }
            });
        }
    }

    /**
     * Identify elements on the page that should send analytics payloads and add
     * the appropriate event handlers to them. Note that we loop through this
     * multiple times to allow for several analytics events to be tied to a
     * single element.
     * @param {HTMLElement} context The HTML element to searching within the DOM.
     */
    manageAnalyticsEventListeners(context = document) {
        const elements = context.querySelectorAll('[data-event_name]');

        if (elements.length) {
            elements.forEach((eventElement) => {
                eventElement.removeEventListener('click', this.handleEventElementClick);
                eventElement.addEventListener('click', this.handleEventElementClick);
            });
        }

        this.addChaseClickEvent(context);
    }

    // ============================================================
    // MAIN EVENT BINDING
    // ============================================================

    /**
     * Attaches event handlers for analytics.
     */
    bindEvents() {
        // Identify the site's search form elements and add a handler for
        // submissions to send a "site search" event containing the searched term.
        // Find all forms and filter for search forms
        const forms = document.querySelectorAll('form');
        const searchFormElements = Array.from(forms).filter(form => {
            // Check if it's a search form by various criteria
            return form.getAttribute('role') === 'search' ||
                   form.classList.contains('dr-search-form') ||
                   form.classList.contains('dr-search-form__form') ||
                   form.querySelector('input[type="search"]') ||
                   form.querySelector('input[name="s"]');
        });

        searchFormElements.forEach((searchFormElement) => {
            // Skip if we've already attached a listener to this form
            if (searchFormElement.getAttribute('data-search-tracking-initialized') === 'true') {
                return;
            }

            // Mark this form as initialized to prevent duplicate tracking
            searchFormElement.setAttribute('data-search-tracking-initialized', 'true');
            searchFormElement.setAttribute('data-tracked-as-search', 'true');

            searchFormElement.addEventListener('submit', (e) => {
                // Find the search input field using multiple selectors
                const searchField = e.currentTarget.querySelector('.dr-search-form__form-input') ||
                                   e.currentTarget.querySelector('input[type="search"]') ||
                                   e.currentTarget.querySelector('input[name*="search"]') ||
                                   e.currentTarget.querySelector('input[name="s"]');

                if (!searchField) {
                    return; // No search field found, skip
                }

                const searchTerm = searchField.value;

                // Update digitalData.search
                if (window.digitalData && window.digitalData.search) {
                    window.digitalData.search.term = searchTerm;
                }

                // Update digitalData.event for site search
                updateDigitalDataEvent({
                    event_name: 'site_search',
                    search_term: searchTerm
                });

                this.trackEvent(
                    'search_performed',
                    {
                        search: {
                            term: searchTerm.toLowerCase(),
                            type: 'internal'
                        },
                        interaction: {
                            linkName: 'Site Search',
                            linkType: 'search',
                            module: 'site-search'
                        }
                    },
                    {
                        event_name: 'site_search',
                        search_term: searchTerm
                    }
                );
            });
        });

        // Track all other form submissions (excluding search forms)
        const allForms = document.querySelectorAll('form:not(.dr-search-form)');

        allForms.forEach((form) => {
            form.addEventListener('submit', () => {
                // Skip if this form is already tracked as a search form
                if (form.hasAttribute('data-tracked-as-search')) {
                    return;
                }

                // Skip if this is a search form (check by class or by presence of search input)
                if (form.classList.contains('dr-search-form') ||
                    form.querySelector('.dr-search-form__form-input') ||
                    form.querySelector('input[type="search"]') ||
                    form.querySelector('input[name*="search"]') ||
                    form.querySelector('input[name="s"]') ||
                    form.getAttribute('role') === 'search') {
                    return;
                }

                // Get form identifier (name, id, or generate from action)
                const formName = form.getAttribute('name') ||
                                form.getAttribute('id') ||
                                form.getAttribute('data-form-name') ||
                                form.getAttribute('action')?.split('/').pop() ||
                                'unnamed-form';

                const formAction = form.getAttribute('action') || window.location.pathname;
                const formMethod = form.getAttribute('method')?.toUpperCase() || 'GET';

                this.trackEvent(
                    'form_submitted',
                    {
                        form: {
                            name: formName,
                            action: formAction,
                            method: formMethod
                        },
                        interaction: {
                            linkName: `Form: ${formName}`,
                            linkType: 'form',
                            linkPosition: 'form-submission',
                            module: 'form-tracking'
                        }
                    },
                    {
                        event_name: 'form_submission',
                        form_name: formName,
                        form_action: formAction,
                        module: 'form-tracking'
                    }
                );
            });
        });

        // Track file downloads (PDFs, docs, images, etc.)
        this.initializeDownloadTracking();

        // Track social sharing clicks
        this.initializeSocialShareTracking();

        // Track custom block interactions (carousel, tabs, modals, etc.)
        this.initializeBlockTracking();

        // Track calculator usage when #calculator hash is used
        this.initializeCalculatorHashTracking();

        // Track card array carousel navigation clicks
        this.initializeCardCarouselNavTracking();

        // Track video interactions
        this.initializeVideoTracking();

        addCustomEvents([
            {
                name: 'crraFootnotesChange',
                callback: this.onCrraFootnotesChange.bind(this)
            },
            {
                name: 'crraLocationChange',
                callback: this.onCrraLocationChange.bind(this)
            },
            {
                name: 'footnotesProcessed',
                callback: this.onFootnotesProcessed.bind(this)
            },
            {
                name: 'sendAnalyticsData',
                callback: this.onSendAnalyticsData.bind(this)
            },
            {
                name: 'trackingCodesReady',
                callback: this.onTrackingCodesReady.bind(this)
            },
            {
                name: 'replaceTokens',
                callback: this.onReplaceTokens.bind(this)
            }
        ]);
    }

    /**
     * Dispatches an analytics payload populated from the analytics-related data
     * attributes applied to the target element that triggered the handler.
     * @param {Event} e The user interaction that triggered this logic.
     */
    handleEventElementClick(e) {
        const target = e.currentTarget;

        // Find all of the attributes to use from the element dataset keys.
        const eventKeys = Object.keys(target.dataset).filter((x) => x.includes('event_name'));

        if (eventKeys.length) {
            eventKeys.forEach((key) => {
                const eventName = target.dataset[key];

                // Initialize a basic analytics data payload containing the event name.
                let payload = {
                    event_name: eventName
                };

                // Check if the current event matches anything in event catalog.
                const matchingEvent = window.disneyRewardsAnalytics.eventCatalog.find(({ type }) => eventName === type);

                // Merge any matching event's custom payload to the default one.
                if (matchingEvent) {
                    payload = {
                        ...payload,
                        ...matchingEvent.getPayload(target)
                    };
                }

                // Update digitalData.event object with event-specific data
                updateDigitalDataEvent(payload);

                // Push to Adobe Client Data Layer (ACDL)
                if (window.pushToDataLayer) {
                    const linkName = target.dataset.navigation_link || target.dataset.footer_link || target.dataset.content_link || target.textContent?.trim() || '';
                    const linkURL = target.href || target.dataset.link_url || '';
                    const linkType = this.getLinkType(target);
                    const linkPosition = this.getLinkPosition(target);
                    const cardType = target.getAttribute('data-card-type') || '';

                    // Determine module based on link type if not provided
                    let module = payload.module || '';
                    if (!module) {
                        if (linkType === 'footer') {
                            module = 'footer-navigation';
                        } else if (linkType === 'navigation') {
                            module = 'main-navigation';
                        } else if (linkType === 'content') {
                            module = 'content-links';
                        }
                    }

                    window.pushToDataLayer({
                        event: this.getAcdlEventName(eventName),
                        product: {
                            cardType
                        },
                        interaction: {
                            linkName,
                            linkURL,
                            linkType,
                            linkPosition,
                            module
                        }
                    });
                }

                sendAnalyticsData(payload);
            });
        }
    }

    // ============================================================
    // INITIALIZATION METHODS
    // ============================================================

    /**
     * Initialize file download tracking for various file types.
     * Tracks clicks on links to downloadable files (PDFs, docs, images, etc.)
     */
    initializeDownloadTracking() {
        // Capture 'this' context for use inside event listeners
        const self = this;

        // Define file extensions to track
        const downloadableExtensions = [
            // Documents
            'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
            'txt', 'rtf', 'csv',
            // Images (high-res downloads)
            'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'tif', 'tiff',
            // Archives
            'zip', 'rar', '7z', 'tar', 'gz',
            // Media
            'mp3', 'mp4', 'wav', 'avi', 'mov', 'wmv',
            // Other
            'xml', 'json', 'ics'
        ];

        // Create regex pattern for file extensions
        const extensionPattern = new RegExp(`\\.(${downloadableExtensions.join('|')})([?#]|$)`, 'i');

        // Find all links that point to downloadable files
        const downloadLinks = Array.from(document.querySelectorAll('a[href]')).filter((link) => {
            const href = link.getAttribute('href');
            return href && extensionPattern.test(href);
        });

        // Add click tracking to download links
        downloadLinks.forEach((link) => {
            link.addEventListener('click', () => {
                const href = link.getAttribute('href');
                const fileName = href.split('/').pop().split('?')[0]; // Get filename without query params
                const fileExtension = fileName.split('.').pop().toLowerCase();
                const linkText = link.textContent?.trim() || fileName;

                // Determine file type category
                let fileCategory = 'document';
                if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'tif', 'tiff'].includes(fileExtension)) {
                    fileCategory = 'image';
                } else if (['mp3', 'mp4', 'wav', 'avi', 'mov', 'wmv'].includes(fileExtension)) {
                    fileCategory = 'media';
                } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(fileExtension)) {
                    fileCategory = 'archive';
                }

                this.trackEvent(
                    'file_downloaded',
                    {
                        download: {
                            fileName,
                            fileType: fileExtension,
                            fileCategory,
                            fileURL: href
                        },
                        interaction: {
                            linkName: linkText,
                            linkURL: href,
                            linkType: 'download',
                            linkPosition: self.getLinkPosition(link),
                            module: 'file-download'
                        }
                    },
                    {
                        event_name: 'file_download',
                        file_name: fileName,
                        file_type: fileExtension,
                        file_category: fileCategory,
                        link_url: href,
                        module: 'file-download'
                    }
                );
            });
        });
    }

    /**
     * Initialize social sharing link tracking.
     * Tracks clicks on social media share buttons and links.
     */
    initializeSocialShareTracking() {
        // Capture 'this' context for use inside event listeners
        const self = this;

        // Common social media patterns
        const socialPatterns = [
            { name: 'Facebook', pattern: /facebook\.com/i },
            { name: 'Twitter', pattern: /twitter\.com|x\.com/i },
            { name: 'LinkedIn', pattern: /linkedin\.com/i },
            { name: 'Pinterest', pattern: /pinterest\.com/i },
            { name: 'Instagram', pattern: /instagram\.com/i },
            { name: 'YouTube', pattern: /youtube\.com|youtu\.be/i },
            { name: 'WhatsApp', pattern: /wa\.me\/|whatsapp\.com/i },
            { name: 'Email', pattern: /^mailto:/i }
        ];

        // Common social share selectors
        // Include specific selectors for social list items with links inside
        const socialSelectors = [
            'a[data-share]',
            'a[data-social]',
            '.social-item a',           // Links inside .social-item list items
            '.social-list a',           // Links inside social lists
            '[class*="share"] a',       // Links inside share containers
            '[class*="social"] a',      // Links inside social containers
            'a[class*="share"]',        // Links with share class
            'a[class*="social"]'        // Links with social class
        ].join(', ');

        // Find potential social share links
        const socialLinks = document.querySelectorAll(socialSelectors);

        socialLinks.forEach((link) => {
            // Check if it's actually a link
            if (link.tagName !== 'A' && link.tagName !== 'BUTTON') {
                return;
            }

            // Skip if we've already attached a listener to this element
            if (link.getAttribute('data-social-tracking-initialized') === 'true') {
                return;
            }

            // Mark this element as initialized
            link.setAttribute('data-social-tracking-initialized', 'true');

            link.addEventListener('click', (e) => {
                // Stop propagation to prevent footer link tracking from firing
                e.stopPropagation();

                const href = link.getAttribute('href') || '';
                const dataNetwork = link.getAttribute('data-network') || link.getAttribute('data-social');

                // Determine social network
                let socialNetwork = dataNetwork || 'Unknown';
                let shareURL = '';
                let shareType = 'link';

                // Try to identify network from href
                if (!dataNetwork) {
                    const matchedSocial = socialPatterns.find((social) => social.pattern.test(href));
                    if (matchedSocial) {
                        socialNetwork = matchedSocial.name;
                    }
                }

                // Determine share type and URL
                if (href.startsWith('mailto:')) {
                    shareType = 'email';
                    socialNetwork = 'Email';
                } else if (href) {
                    shareURL = href;
                    shareType = 'link';
                }

                // Get shared content information
                // Check data attributes first to match footer_link_click naming
                const linkText = link.dataset.footer_link ||
                                link.dataset.navigation_link ||
                                link.dataset.content_link ||
                                link.textContent?.trim() ||
                                link.getAttribute('aria-label') ||
                                socialNetwork;

                // Get page being shared
                const sharedPageURL = window.location.href;
                const sharedPageTitle = document.title;

                this.trackEvent(
                    'social_share',
                    {
                        share: {
                            network: socialNetwork,
                            type: shareType,
                            shareURL,
                            sharedPageURL,
                            sharedPageTitle
                        },
                        interaction: {
                            linkName: linkText,
                            linkURL: href,
                            linkType: 'social',
                            linkPosition: self.getLinkPosition(link),
                            module: 'social-sharing'
                        }
                    },
                    {
                        event_name: 'social_share',
                        social_network: socialNetwork,
                        share_type: shareType,
                        link_url: href,
                        module: 'social-sharing'
                    }
                );
            });
        });
    }

    /**
     * Initialize centralized tracking for custom block interactions.
     * Listens for CustomEvents emitted by blocks (carousel, tabs, modals, etc.)
     */
    initializeBlockTracking() {
        // Carousel slide changes
        document.addEventListener('carouselChange', this.handleCarouselChange.bind(this));

        // Card selector interactions
        window.addEventListener('cardSelection', this.handleCardSelection.bind(this));

        // Accordion toggle
        document.addEventListener('accordionToggled', this.handleAccordionToggle.bind(this));

        // Modal open/close
        document.addEventListener('modalOpened', this.handleModalOpen.bind(this));
        document.addEventListener('modalClosed', this.handleModalClose.bind(this));

        // Dialog open/close
        document.addEventListener('dialogOpened', this.handleDialogOpen.bind(this));
        document.addEventListener('dialogClosed', this.handleDialogClose.bind(this));

        // Tab switching
        document.addEventListener('drTabChanged', this.handleTabChange.bind(this));

        // Transfer DRD carts
        window.addEventListener('drTransferDrdCarts', this.handleTransferDrdCarts.bind(this));

        // Transfer DRD checkouts
        window.addEventListener('drTransferDrdSubmit', this.handleTransferDrdSubmit.bind(this));

        // Transfer DRD order completion
        window.addEventListener('drTransferDrdOrderComplete', this.handleTransferDrdOrderComplete.bind(this));

        // Calculator users
        window.addEventListener('calculatorUsers', this.handleCalculatorUsers.bind(this));

        // Quiz completion
        window.addEventListener('quizComplete', this.handleQuizComplete.bind(this));

        // Card type selection
        window.addEventListener('cardTypeSelection', this.handleCardTypeSelection.bind(this));
    }

    /**
     * Initialize calculate hash tracking.
     * Tracks when users navigate to #calculate hash.
     *
     * Note: We rely on the hashchange event instead of click listeners to avoid
     * duplicate tracking (clicking a #calculate link triggers both click AND hashchange).
     */
    initializeCalculatorHashTracking() {
        // Track when page loads with #calculate hash
        if (window.location.hash === '#calculate') {
            this.handleCalculatorUsers();
        }

        // Track hash changes to #calculate (handles clicks on #calculate links and direct navigation)
        window.addEventListener('hashchange', () => {
            if (window.location.hash === '#calculate') {
                this.handleCalculatorUsers();
            }
        });
    }

    /**
     * Initialize card carousel navigation tracking.
     * Tracks when users click on carousel navigation arrows (prev/next).
     */
    initializeCardCarouselNavTracking() {
        // Track clicks on carousel navigation buttons
        document.addEventListener('click', (e) => {
            const navButton = e.target.closest('.dr-visa-card-carousel__nav');
            if (navButton) {
                // Find the carousel container to get context
                const carousel = navButton.closest('.dr-visa-card-carousel');
                const carouselTitle = carousel?.querySelector('.dr-visa-card-carousel__title')?.textContent?.trim() || 'Card Carousel';

                // Find the active slide (card in the middle of the carousel)
                const activeSlide = carousel?.querySelector('.dr-visa-card-carousel__slide.swiper-slide-active');

                // Extract cardType and cardArt from the active slide
                const cardType = activeSlide?.getAttribute('data-card-type') || '';
                const cardArt = activeSlide?.getAttribute('data-design-slug') || '';

                this.trackEvent(
                    'card_array_click',
                    {
                        product: {
                            cardType,
                            cardArt
                        },
                        interaction: {
                            linkName: `Card Array: ${cardArt}`,
                            linkType: 'card-interaction'
                        }
                    },
                    {
                        event_name: 'card_array_click',
                        card_type: cardType,
                        card_art: cardArt,
                        carousel_title: carouselTitle,
                        module: 'card-carousel'
                    }
                );
            }
        });
    }

    /**
     * Initialize video tracking.
     * Tracks video start and complete events for all videos on the page.
     */
    initializeVideoTracking() {
        // Find all video elements on the page
        const videos = document.querySelectorAll('video');

        videos.forEach((video) => {
            // Track if this video has already been tracked to avoid duplicates
            if (video.hasAttribute('data-video-tracking-initialized')) {
                return;
            }
            video.setAttribute('data-video-tracking-initialized', 'true');

            // Track video start (only once per video)
            video.addEventListener('play', () => {
                // Check if video start has already been tracked
                if (video.hasAttribute('data-video-started')) {
                    return;
                }

                // Mark video as started to prevent duplicate tracking
                video.setAttribute('data-video-started', 'true');

                const { videoTitle, videoURL, videoDuration } = this.getVideoMetadata(video);

                this.trackEvent(
                    'video_start',
                    {
                        video: {
                            title: videoTitle,
                            url: videoURL,
                            duration: videoDuration,
                            player: 'HTML5'
                        },
                        interaction: {
                            linkName: `Video: ${videoTitle}`,
                            linkType: 'video',
                            module: 'video-player'
                        }
                    },
                    {
                        event_name: 'video_start',
                        video_title: videoTitle,
                        video_url: videoURL,
                        video_duration: videoDuration,
                        module: 'video-player'
                    }
                );
            });

            // Track video complete
            video.addEventListener('ended', () => {
                const { videoTitle, videoURL, videoDuration } = this.getVideoMetadata(video);

                this.trackEvent(
                    'video_complete',
                    {
                        video: {
                            title: videoTitle,
                            url: videoURL,
                            duration: videoDuration,
                            player: 'HTML5'
                        },
                        interaction: {
                            linkName: `Video: ${videoTitle}`,
                            linkType: 'video',
                            module: 'video-player'
                        }
                    },
                    {
                        event_name: 'video_complete',
                        video_title: videoTitle,
                        video_url: videoURL,
                        video_duration: videoDuration,
                        module: 'video-player'
                    }
                );
            });
        });

        // Create MutationObserver only once to watch for dynamically added videos
        if (!this.videoTrackingObserver) {
            this.videoTrackingObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) { // Element node
                            // Check if the added node is a video
                            if (node.tagName === 'VIDEO') {
                                this.initializeVideoTracking();
                            }
                            // Check if the added node contains videos
                            if (node.querySelectorAll) {
                                const newVideos = node.querySelectorAll('video');
                                if (newVideos.length > 0) {
                                    this.initializeVideoTracking();
                                }
                            }
                        }
                    });
                });
            });

            // Start observing the document for video additions
            this.videoTrackingObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    // ============================================================
    // EVENT HANDLERS
    // ============================================================

    /**
     * Handle carousel slide change events
     * @param {CustomEvent} e - The carousel change event
     */
    handleCarouselChange(e) {
        const { activeSlide, currentIndex, totalSlides } = e.detail;
        const cardTitle = activeSlide?.querySelector('.dr-visa-card-carousel__card-title')?.textContent?.trim();

        pushToDataLayer({
            event: 'carousel_slide_changed',
            interaction: {
                linkName: cardTitle || `Slide ${currentIndex + 1}`,
                linkType: 'carousel',
                linkPosition: 'carousel-navigation',
                module: 'card-carousel'
            },
            content: {
                carouselSlideIndex: currentIndex,
                carouselTotalSlides: totalSlides,
                carouselSlideTitle: cardTitle || ''
            }
        });
    }

    /**
     * Handle card selection events from card selector block
     * @param {CustomEvent} e - The card selection event
     */
    handleCardSelection(e) {
        const { cardType, cardArt } = e.detail;

        pushToDataLayer({
            event: 'card_art_selected',
            product: {
                cardType: cardType || '',
                cardArt: cardArt || ''
            },
            interaction: {
                linkName: cardArt || cardType || 'Card Selection',
                linkType: 'card selector',
                linkPosition: 'card-selector',
                module: 'card-gallery'
            }
        });
    }

    /**
     * Handle card type selection events from card selector block
     * @param {CustomEvent} e - The card type selection event
     */
    handleCardTypeSelection(e) {
        const { cardType } = e.detail;

        pushToDataLayer({
            event: 'card_type_click',
            product: {
                cardType: cardType || '',
            }
        });
    }

    /**
     * Handle accordion toggle events
     * @param {CustomEvent} e - The accordion toggle event
     */
    handleAccordionToggle(e) {
        const { action, title } = e.detail;

        pushToDataLayer({
            event: `accordion_${action}`,
            interaction: {
                linkName: title || 'Accordion Item',
                linkType: 'accordion',
                linkPosition: 'content',
                module: 'custom-accordion'
            },
            content: {
                accordionAction: action,
                accordionTitle: title || ''
            }
        });
    }

    /**
     * Handle modal opened events
     * @param {CustomEvent} e - The modal opened event
     */
    handleModalOpen(e) {
        const { title, modalType, element } = e.detail;

        pushToDataLayer({
            event: 'modal_opened',
            interaction: {
                linkName: title || 'Modal',
                linkType: 'modal',
                linkPosition: 'overlay',
                module: `modal-${modalType || 'generic'}`
            },
            content: {
                modalType: modalType || 'generic',
                modalTitle: title || ''
            }
        });

        // Check if the modal contains a calculator block
        if (element && element.querySelector('.dr-block-section-calculator')) {
            // Fire calculator users event
            this.handleCalculatorUsers();
        }
    }

    /**
     * Handle modal closed events
     * @param {CustomEvent} e - The modal closed event
     */
    handleModalClose(e) {
        const { title, modalType } = e.detail;

        pushToDataLayer({
            event: 'modal_closed',
            interaction: {
                linkName: title || 'Modal',
                linkType: 'modal',
                linkPosition: 'overlay',
                module: `modal-${modalType || 'generic'}`
            },
            content: {
                modalType: modalType || 'generic',
                modalTitle: title || ''
            }
        });
    }

    /**
     * Handle dialog opened events
     * @param {CustomEvent} e - The dialog opened event
     */
    handleDialogOpen(e) {
        const { title } = e.detail;

        pushToDataLayer({
            event: 'dialog_opened',
            interaction: {
                linkName: title || 'Dialog',
                linkType: 'dialog',
                linkPosition: 'overlay',
                module: 'dialog-bones'
            },
            content: {
                modalType: 'dialog',
                modalTitle: title || ''
            }
        });
    }

    /**
     * Handle dialog closed events
     * @param {CustomEvent} e - The dialog closed event
     */
    handleDialogClose(e) {
        const { title } = e.detail;

        pushToDataLayer({
            event: 'dialog_closed',
            interaction: {
                linkName: title || 'Dialog',
                linkType: 'dialog',
                linkPosition: 'overlay',
                module: 'dialog-bones'
            },
            content: {
                modalType: 'dialog',
                modalTitle: title || ''
            }
        });
    }

    /**
     * Handle tab change events
     * @param {CustomEvent} e - The tab change event
     */
    handleTabChange(e) {
        const { activeIndex, activeButton } = e.detail;
        const tabLabel = activeButton?.textContent?.trim() || `Tab ${activeIndex + 1}`;

        pushToDataLayer({
            event: 'tab_switched',
            interaction: {
                linkName: tabLabel,
                linkType: 'tab',
                linkPosition: 'tab-navigation',
                module: 'section-tabs'
            },
            content: {
                tabIndex: activeIndex,
                tabLabel
            }
        });
    }

    /**
     * Handles the "Transfer DRD Carts" event.
     * This method tracks a "cart_quickview" event with product details such as revenue
     * and a product string, which includes information about the redemption card type
     * and transfer amount.
     *
     * @param {CustomEvent} e - The custom event containing details about the transfer.
     * @property {Object} e.detail - The event details.
     * @property {number} e.detail.transferAmount - The amount being transferred.
     * @property {string} e.detail.cardType - The type of the redemption card.
     * @property {boolean} e.detail.isNewRedemptionCard - Indicates if the redemption card is new.
     */
    handleTransferDrdCarts(e) {
        const { detail } = e;

        const transferAmount = detail.transferAmount?.toFixed(2) || '0.00';
        const cardType = detail.cardType || '';
        const productString = `DRRC;${detail.isNewRedemptionCard ? 'New DRRC' : 'Existing DRRC'};1;${transferAmount};;eVar82=DRRC|eVar83=${detail.isNewRedemptionCard ? 'New DRRC' : 'Existing DRRC'}`;
        const acdlData = {
            product: {
                cardType,
                type: detail.isNewRedemptionCard ? 'New Card Order' : 'Existing Card Points Transfer',
                revenue: transferAmount,
                productString
            }
        };

        this.trackEvent(
            'carts',
            acdlData
        );
    }

    /**
     * Handles the "Transfer DRD Submit" event.
     * This method tracks a "checkouts" event with product details such as revenue.
     *
     * @param {CustomEvent} e - The custom event containing details about the transfer.
     * @property {Object} e.detail - The event details.
     * @property {number} e.detail.transferAmount - The amount being transferred.
     * @property {string} e.detail.cardType - The type of the redemption card.
     * @property {boolean} e.detail.isNewRedemptionCard - Indicates if the redemption card is new.
     */
    handleTransferDrdSubmit(e) {
        const { detail } = e;

        const transferAmount = detail.transferAmount?.toFixed(2) || '0.00';
        const cardType = detail.cardType || '';
        const productString = `DRRC;${detail.isNewRedemptionCard ? 'New DRRC' : 'Existing DRRC'};1;${transferAmount};;eVar82=DRRC|eVar83=${detail.isNewRedemptionCard ? 'New DRRC' : 'Existing DRRC'}`;
        const acdlData = {
            product: {
                cardType,
                type: detail.isNewRedemptionCard ? 'New Card Order' : 'Existing Card Points Transfer',
                productString,
                revenue: transferAmount
            }
        };

        this.trackEvent(
            'checkouts',
            acdlData
        );
    }

    /**
     * Handle transfer-drd order completion events
     * Fires "purchase" for completed orders
     * @param {CustomEvent} e - The custom event with order details
     * @property {string} e.detail.orderNumber - The order number of the completed order
     * @property {number} e.detail.transferAmount - The amount being transferred
     * @property {string} e.detail.cardType - The type of the redemption card
     * @property {boolean} e.detail.isNewRedemptionCard - Indicates if the redemption card is new
     */
    handleTransferDrdOrderComplete(e) {
        const { detail } = e;

        if (!detail?.orderNumber) {
            return;
        }

        const transferAmount = detail.transferAmount?.toFixed(2) || '0.00';
        const cardType = detail.cardType || '';
        const productString = `DRRC;${detail.isNewRedemptionCard ? 'New DRRC' : 'Existing DRRC'};1;${transferAmount};;eVar82=DRRC|eVar83=${detail.isNewRedemptionCard ? 'New DRRC' : 'Existing DRRC'}`;

        const acdlData = {
            product: {
                cardType,
                type: detail.isNewRedemptionCard ? 'New Card Order' : 'Existing Card Points Transfer',
                revenue: transferAmount,
                productString,
                purchaseId: detail.orderNumber
            }
        };

        this.trackEvent(
            'purchase',
            acdlData
        );
    }

    /**
     * Handle calculator use events
     * Note: This fires when the calculator becomes available/visible to the user,
     * not when they actually submit values. Actual calculation tracking would
     * require hooking into the calculator's submit/calculation event.
     */
    handleCalculatorUsers() {
        this.trackEvent(
            'calculator_users',
            {
                interaction: {
                    linkName: 'calculate',
                    linkType: 'calculator',
                    module: 'rewards-calculator'
                }
            },
            {
                event_name: 'calculator_users',
                module: 'rewards-calculator'
            }
        );
    }

    /**
     * Handle quiz completion events
     * @param {CustomEvent} e - The quiz completion event
     */
    handleQuizComplete(e) {
        const { quizType, quizTitle, score, resultPath } = e.detail;

        let quizResult = '';
        if (quizType === 'trivia') {
            quizResult = score;
        } else if (quizType === 'logic') {
            quizResult = resultPath;
        }

        pushToDataLayer({
            event: 'quiz_complete',
            form: {
                name: quizTitle,
                result: quizResult
            }
        });

        const legacyData = {
            event_name: 'quiz_complete',
            quiz_type: quizType,
            module: 'quiz-completion'
        };

        sendAnalyticsData(legacyData);
    }

    // ============================================================
    // CUSTOM EVENT CALLBACKS
    // ============================================================

    onCrraFootnotesChange() {
        setTimeout(() => {
            const footnotes = document.querySelector('.footnotes');

            if (footnotes) {
                this.manageAnalyticsEventListeners(footnotes);
            }
        }, contentWaitTimeout);
    }

    onCrraLocationChange() {
        setTimeout(() => {
            this.manageAnalyticsEventListeners(document.getElementById('crra'));
        }, contentWaitTimeout);
    }

    onFootnotesProcessed() {
        const footnotes = document.querySelector('.footnotes');

        if (footnotes) {
            this.manageAnalyticsEventListeners(footnotes);
        }
    }

    onSendAnalyticsData(e) {
        updateDigitalDataEvent(e.detail);
        sendAnalyticsData(e.detail);
    }

    onTrackingCodesReady() {
        if (!window.analytics_data?.page_name?.includes('cardmember_rewards')) {
            sendAnalyticsData(
                {
                    ...window.analytics_data,
                    cell: getCell().value,
                    page_view: true
                },
                this.analyticsTimeout
            );
        }
    }

    onReplaceTokens(e) {
        this.addChaseClickEvent(e.detail.element.parentElement);
    }
}

// ============================================================
// DOM CONTENT LOADED - PAGE INITIALIZATION
// ============================================================

(() => {
    document.addEventListener('DOMContentLoaded', async () => {
        // Initialize digitalData with client-side values (legacy)
        initializeDigitalData();

        // Expose ACDL helper functions globally for use in page templates and components
        // pushToDataLayer is already available globally from Analytics.php
        // No need to redefine it - just ensure it exists
        if (!window.pushToDataLayer) {
            Object.defineProperty(window, 'pushToDataLayer', { value: pushToDataLayer, writable: false });
        }
        Object.defineProperty(window, 'trackOneIdEvent', { value: trackOneIdEvent, writable: false });
        Object.defineProperty(window, 'getOneIdData', { value: getOneIdData, writable: false });

        window.disneyRewardsAnalytics = new DisneyRewardsAnalytics();

        // Track pageview for all pages
        const basePageName = window.digitalData?.page?.pageInfo?.pageName || document.title || 'Unknown Page';
        const isInAppRestricted = !!(window.redirectionSettings?.restrict && window.redirectionSettings?.inAppBrowser);
        const pageName = isInAppRestricted ? `in_app_restricted | ${basePageName}` : basePageName;
        const pageURL = window.digitalData?.page?.pageInfo?.pageURL || window.location.href;
        const pageSection = window.digitalData?.page?.pageInfo?.pageSection || '';
        const referringURL = window.digitalData?.page?.pageInfo?.referringURL || document.referrer || '';
        const responsive = window.digitalData?.page?.attributes?.responsive;
        const pageType = window.digitalData?.page?.pageInfo?.pageType || 'content';
        const server = window.digitalData?.page?.pageInfo?.server || '';
        const language = window.digitalData?.page?.pageInfo?.language || 'en-US';
        const geoRegion = 'US';
        // user profile
        const status = window.digitalData?.user?.profile?.status;
        const loginStatus = window.digitalData?.user?.profile?.loginStatus;
        const swid = window.digitalData?.user?.profile?.swid;
        const unid = window.digitalData?.user?.profile?.unid;
        const coreID = window.digitalData?.user?.profile?.coreID;
        const ecid = window.digitalData?.user?.profile?.ecid;
        const userType = window.digitalData?.user?.profile?.type;
        // campaign
        const name = window.digitalData?.campaign?.name;
        const cell = window.digitalData?.campaign?.cellcode;
        const code = window.digitalData?.campaign?.code;
        const medium = window.digitalData?.campaign?.medium;
        const publisher = window.digitalData?.campaign?.publisher;
        const creative = window.digitalData?.campaign?.creative;
        const creativePlacement = window.digitalData?.campaign?.creativePlacement;
        const type = window.digitalData?.campaign?.type;
        const date = window.digitalData?.campaign?.date;
        // product
        const cardType = window.digitalData?.product?.cardType;
        const sourceCode = window.digitalData?.product?.sourceCode;
        // search
        const term = window.digitalData?.search?.term;
        // technical
        const breakpoint = window.digitalData?.technical?.breakpoint;

        pushToDataLayer({
            event: 'pageview',
            page: {
                pageInfo: {
                    pageName,
                    pageURL,
                    pageType,
                    pageSection,
                    referringURL,
                    server,
                    language,
                    geoRegion
                },
                attributes: {
                    pageURL,
                    responsive,
                    in_app_browser: window.redirectionSettings?.inAppBrowser || ''
                }
            },
            user: {
                profile: {
                    status,
                    loginStatus,
                    swid,
                    unid,
                    coreID,
                    ecid,
                    type: userType
                }
            },
            campaign: {
                name,
                cell,
                code,
                medium,
                publisher,
                creative,
                creativePlacement,
                type,
                date
            },
            product: {
                cardType,
                sourceCode
            },
            search: {
                term
            },
            technical: {
                breakpoint
            }
        });

        // Legacy tracking
        sendAnalyticsData({
            event_name: 'page_view',
            page_name: pageName,
            page_url: window.location.href,
            page_path: window.location.pathname,
            referring_url: document.referrer || '',
            page_section: pageSection
        });

        // Track 404 error page if this is a 404
        if (window.digitalData?.page?.pageInfo?.pageType === 'errorPage' ||
            window.digitalData?.page?.pageInfo?.pageName === '404_page') {

            pushToDataLayer({
                event: '404_error',
                page: {
                    pageInfo: {
                        pageName: window.digitalData?.page?.pageInfo?.pageName || '404_page',
                        pageType: 'errorPage',
                        pageURL: window.location.href,
                        referringURL: document.referrer || ''
                    }
                },
                error: {
                    type: '404',
                    message: 'Page not found',
                    url: window.location.href
                }
            });

            // Legacy tracking
            sendAnalyticsData({
                event_name: '404_error',
                page_name: '404_page',
                page_type: 'errorPage',
                error_type: '404',
                page_url: window.location.href,
                referring_url: document.referrer || ''
            });
        }

        // Track compare cards click page view
        const currentPath = window.location.pathname;
        if (currentPath === '/compare-credit-cards/' || currentPath === '/compare-disney-credit-cards-pc/') {
            pushToDataLayer({
                event: 'compare_cards_click',
                interaction: {
                    linkName: 'Compare Cards',
                    linkType: 'tool'
                }
            });

            // Legacy tracking
            sendAnalyticsData({
                event_name: 'compare_cards_click',
                page_name: window.digitalData?.page?.pageInfo?.pageName || 'compare_cards_click',
                page_url: window.location.href,
                page_type: 'comparison',
                module: 'credit-card-comparison'
            });
        }

        // Track member account page view
        if (currentPath === '/manage-account/') {
            if (sessionStorage.getItem('dr_pending_login_completed') === '1') {
                sessionStorage.removeItem('dr_pending_login_completed');

                pushToDataLayer({
                    event: 'login_completed',
                    interaction: {
                        linkName: 'Login',
                        linkPosition: 'manage-account-login',
                        linkType: 'cta',
                        module: 'authentication'
                    }
                });
            }
            pushToDataLayer({
                event: 'member_account_click',
                interaction: {
                    linkName: 'Member Account Page View',
                    linkType: 'page view',
                    module: 'account-management'
                }
            });

            // Legacy tracking
            sendAnalyticsData({
                event_name: 'member_account_click',
                page_name: window.digitalData?.page?.pageInfo?.pageName || 'manage_account',
                page_url: window.location.href,
                page_type: 'account management',
                module: 'account-management'
            });
        }
    });
})();
